import { useId, useRef, type KeyboardEvent, type MouseEvent } from "react";
import { Link } from "react-router-dom";
import { formatPriceCents } from "../../lib/money";
import type { InvoiceRecord } from "../jobs/types";
import { InvoicePaymentsPanel } from "./InvoicePaymentsPanel";
import { registerState, stateBadgeVariant, stateLabel } from "./lib/invoiceState";
import { documentDate } from "./lib/registerFilters";
import styles from "./DocumentRows.module.css";
import type { DocumentListRecord } from "./types";

const dateFormatter = new Intl.DateTimeFormat("en-AU", {
	day: "numeric",
	month: "short",
	year: "numeric",
});

export function formatDate(value: string | null): string {
	if (!value) return "—";
	const date = new Date(value);
	return Number.isNaN(date.getTime()) ? "—" : dateFormatter.format(date);
}

function documentLabel(document: DocumentListRecord): string {
	if (document.kind === "quote" && document.version) {
		return `${document.number} · v${document.version}`;
	}
	return document.number;
}

export interface DocumentRowProps {
	document: DocumentListRecord;
	/** Cells in the table, so the detail row's colSpan cannot drift from it. */
	columnCount: number;
	/** Flat mode carries a Job column where grouped mode has a banner. */
	showJobColumn: boolean;
	expanded: boolean;
	/** The lazily-fetched invoice behind an expanded row; null while loading. */
	detail: InvoiceRecord | null;
	detailError: string | null;
	/** Puts the cursor in the payment amount when the row was opened to pay. */
	autoFocusAmount: boolean;
	/** This row has an action in flight; its own buttons stand down. */
	busy: boolean;
	/** Send is armed on this row: the next click is the real one. */
	sendArmed: boolean;
	onToggle: (document: DocumentListRecord) => void;
	onEdit: (document: DocumentListRecord) => void;
	onSendClick: (document: DocumentListRecord) => void;
	onSendBlur: () => void;
	onEmail: (document: DocumentListRecord) => void;
	onRecordPayment: (document: DocumentListRecord) => void;
	onPaymentRecorded: (document: DocumentListRecord) => void;
}

/**
 * One register row, plus the detail row it discloses.
 *
 * The disclosure is a real button with an accessible name — the whole `<tr>`
 * also responds to a click, but only as a convenience for the mouse, so it
 * carries no role and no tabIndex and `aria-expanded` stays on the button where
 * it is valid.
 *
 * Quotes do not expand: this epic is about invoices, and there is nothing
 * behind a quote row worth a second layout and a second endpoint.
 */
export function DocumentRow({
	document,
	columnCount,
	showJobColumn,
	expanded,
	detail,
	detailError,
	autoFocusAmount,
	busy,
	sendArmed,
	onToggle,
	onEdit,
	onSendClick,
	onSendBlur,
	onEmail,
	onRecordPayment,
	onPaymentRecorded,
}: DocumentRowProps) {
	const detailId = useId();
	const disclosureRef = useRef<HTMLButtonElement | null>(null);
	const isInvoice = document.kind === "invoice";
	const state = registerState(document);
	const paidCents = document.paidCents ?? 0;
	const balanceCents = document.balanceCents ?? 0;
	const partPaid = state === "part-paid";
	const pdfHref = `/api/${document.kind === "quote" ? "quotes" : "invoices"}/${document.id}/pdf`;

	const handleRowClick = (event: MouseEvent<HTMLTableRowElement>): void => {
		if (!isInvoice) return;
		// A click that landed on a link or a button was meant for that control.
		if ((event.target as HTMLElement).closest("a,button")) return;
		onToggle(document);
	};

	const handleDetailKeyDown = (event: KeyboardEvent<HTMLTableRowElement>): void => {
		if (event.key !== "Escape") return;
		event.stopPropagation();
		onToggle(document);
		disclosureRef.current?.focus();
	};

	const actions = (variant: "row" | "footer") => {
		// The footer sits in the opened detail, already a low-emphasis context, so
		// every action there stays a quiet text button. In the row itself the
		// actions compete for the eye, so exactly one — the invoice's next step —
		// is filled and the rest are outline.
		const isRow = variant === "row";
		const primaryClass = isRow ? styles.actionPrimary : styles.footerAction;
		const secondaryClass = isRow ? styles.action : styles.footerAction;
		const linkClass = isRow ? styles.actionLink : styles.footerAction;
		return (
			<>
				{isInvoice && state === "draft" && (
					<>
						<button
							type="button"
							className={secondaryClass}
							disabled={busy}
							onClick={() => onEdit(document)}
						>
							Edit
						</button>
						<button
							type="button"
							className={primaryClass}
							disabled={busy}
							onClick={() => onSendClick(document)}
							onBlur={onSendBlur}
						>
							{busy ? "Sending…" : sendArmed ? "Confirm send" : "Send"}
						</button>
					</>
				)}
				{isInvoice && state === "sent" && (
					<>
						<button
							type="button"
							className={secondaryClass}
							disabled={busy}
							onClick={() => onEdit(document)}
						>
							Edit
						</button>
						<button
							type="button"
							className={primaryClass}
							disabled={busy}
							onClick={() => onRecordPayment(document)}
						>
							Record payment
						</button>
					</>
				)}
				{isInvoice && partPaid && (
					<>
						<button
							type="button"
							className={primaryClass}
							disabled={busy}
							onClick={() => onRecordPayment(document)}
						>
							Record payment
						</button>
						<button
							type="button"
							className={secondaryClass}
							disabled={busy}
							onClick={() => onEdit(document)}
						>
							Notes
						</button>
					</>
				)}
				{isInvoice && state === "paid" && (
					<button
						type="button"
						className={secondaryClass}
						disabled={busy}
						onClick={() => onEdit(document)}
					>
						Notes
					</button>
				)}
				{isInvoice && state !== "draft" && (
					<button
						type="button"
						className={secondaryClass}
						disabled={busy}
						onClick={() => onEmail(document)}
					>
						{busy ? "Working…" : "Email"}
					</button>
				)}
				<a className={linkClass} href={pdfHref} target="_blank" rel="noopener">
					{variant === "row" ? "PDF" : "Open PDF"}
				</a>
			</>
		);
	};

	return (
		<>
			<tr className={expanded ? styles.rowExpanded : undefined} onClick={handleRowClick}>
				<td data-label="Document">
					<span className={styles.documentCell}>
						{isInvoice ? (
							<button
								type="button"
								ref={disclosureRef}
								className={`${styles.disclosure}${expanded ? ` ${styles.disclosureOpen}` : ""}`}
								aria-expanded={expanded}
								aria-controls={detailId}
								onClick={() => onToggle(document)}
							>
								<span className={styles.caret} aria-hidden="true">
									▸
								</span>
								<span className={styles.visuallyHidden}>
									Show line items and payments for {document.number}
								</span>
							</button>
						) : (
							<span className={styles.disclosureSpacer} aria-hidden="true" />
						)}
						<span className={styles.kind}>{document.kind}</span>
						<strong>{documentLabel(document)}</strong>
					</span>
				</td>

				{showJobColumn && (
					<td data-label="Job">
						<span className={styles.cellStack}>
							<span className={styles.cellPrimary}>
								{document.clientName || "No client"}
							</span>
							<span className={styles.cellMeta}>
								{document.propertyAddress || "No property"}
							</span>
							<span className={styles.cellMeta}>
								{document.jobId ? (
									<Link
										className={styles.jobLink}
										to={`/jobs?job=${encodeURIComponent(document.jobId)}`}
									>
										{document.jobRefInternal || "Job without a reference"}
									</Link>
								) : (
									<span className={styles.noJob}>no job</span>
								)}
							</span>
						</span>
					</td>
				)}

				<td data-label="Status">
					<span className={`${styles.badge} ${styles[stateBadgeVariant(state)]}`}>
						{stateLabel(state)}
					</span>
				</td>

				<td data-label="Date">
					<time dateTime={documentDate(document)}>
						{formatDate(documentDate(document))}
					</time>
				</td>

				<td data-label="Total" className={styles.money}>
					{formatPriceCents(document.totalCents)}
				</td>

				<td data-label="Balance" className={styles.money}>
					{partPaid ? (
						<span className={styles.cellStack}>
							<span className={styles.cellPrimary}>
								{formatPriceCents(balanceCents)}
							</span>
							<span className={styles.cellMeta}>
								of {formatPriceCents(document.totalCents)}
							</span>
						</span>
					) : (
						<>
							<span aria-hidden="true">—</span>
							<span className={styles.visuallyHidden}>
								{document.kind === "quote"
									? "Not applicable to a quote"
									: state === "paid"
										? "Paid in full"
										: state === "draft"
											? "Not sent yet"
											: `${formatPriceCents(balanceCents)} outstanding`}
							</span>
						</>
					)}
				</td>

				<td data-label="Actions" className={styles.actions}>
					{actions("row")}
				</td>
			</tr>

			{expanded && (
				<tr className={styles.detailRow} onKeyDown={handleDetailKeyDown}>
					<td colSpan={columnCount} id={detailId}>
						<div className={styles.detail}>
							<div className={styles.detailContent}>
								{detailError ? (
									<p className={styles.error} role="alert">
										{detailError}
									</p>
								) : detail === null ? (
									<p className={styles.note}>Loading invoice…</p>
								) : (
									<>
										<p className={styles.scope}>
											{detail.scopeDescription || "No scope description."}
										</p>
										<table className={styles.itemsTable}>
											<thead>
												<tr>
													<th scope="col">Item</th>
													<th scope="col">Qty</th>
													<th scope="col">Unit</th>
													<th scope="col">Amount</th>
												</tr>
											</thead>
											<tbody>
												{detail.lineItems.map((item) => (
													<tr key={item.id}>
														<td>
															<span className={styles.cellStack}>
																<span className={styles.cellPrimary}>
																	{item.title}
																</span>
																{item.description && (
																	<span className={styles.cellMeta}>
																		{item.description}
																	</span>
																)}
															</span>
														</td>
														<td className={styles.money}>{item.quantity}</td>
														<td className={styles.money}>
															{formatPriceCents(item.unitAmountCents)}
														</td>
														<td className={styles.money}>
															{formatPriceCents(
																item.unitAmountCents * item.quantity,
															)}
														</td>
													</tr>
												))}
											</tbody>
										</table>
										<dl className={styles.totals}>
											<div>
												<dt>Subtotal</dt>
												<dd className={styles.money}>
													{formatPriceCents(detail.subtotalCents)}
												</dd>
											</div>
											<div>
												<dt>GST</dt>
												<dd className={styles.money}>
													{formatPriceCents(detail.taxCents)}
												</dd>
											</div>
											<div>
												<dt>Total</dt>
												<dd className={`${styles.money} ${styles.totalValue}`}>
													{formatPriceCents(detail.totalCents)}
												</dd>
											</div>
										</dl>
										{detail.notes && <p className={styles.notes}>{detail.notes}</p>}
									</>
								)}
							</div>

							<div className={styles.detailPayments}>
								<dl className={styles.dates}>
									<div>
										<dt className={styles.dateLabel}>Issued</dt>
										<dd className={styles.dateValue}>
											{formatDate(document.sentAt ?? document.createdAt)}
										</dd>
									</div>
									<div>
										<dt className={styles.dateLabel}>Emailed (latest)</dt>
										<dd className={styles.dateValue}>
											{formatDate(detail?.emailedAt ?? null)}
										</dd>
									</div>
									<div>
										<dt className={styles.dateLabel}>Paid</dt>
										<dd className={styles.dateValue}>
											{formatDate(detail?.paidAt ?? null)}
										</dd>
									</div>
								</dl>

								{detail && detail.status === "draft" ? (
									<p className={styles.note}>
										Payments can be recorded once this invoice is sent.
									</p>
								) : detail ? (
									<InvoicePaymentsPanel
										invoice={detail}
										autoFocusAmount={autoFocusAmount}
										onChanged={() => onPaymentRecorded(document)}
									/>
								) : null}
								{paidCents > 0 && detail === null && (
									<p className={styles.note}>
										{formatPriceCents(paidCents)} received so far.
									</p>
								)}
							</div>

							<div className={styles.detailFooter}>{actions("footer")}</div>
						</div>
					</td>
				</tr>
			)}
		</>
	);
}
